import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  getDashboardIncidentCompanies,
  getDashboardIncidents,
} from "../../repository/data.repository";
import {
  DashboardIncidentCompany,
  DashboardIncidents,
} from "../../repository/data.default";
import {
  SEVERITY_COLORS,
  STATUS_COLORS,
  renderDualTrend,
  renderStackedColumn,
} from "./MetricChartRender";
import { InsightChartCard, InsightStrip } from "./InsightCards";
import { pickBucket } from "./AnalyticsRange";
import { DashboardSectionScope } from "./DashboardSection";
import { useServerPagedTable } from "./useServerPagedTable";

/** Page size of the per-company load table. */
const COMPANIES_PAGE_SIZE = 8;

// Incident triage from one server-side rollup: the open backlog by age and severity, the
// severity–status picture, which companies carry the load, and the created-vs-resolved flow.
// Backlog figures are point-in-time snapshots; only the flow chart honours the date range.
function IncidentsCard(props: DashboardSectionScope) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [incidents, setIncidents] = useState<DashboardIncidents | undefined>();

  const from = props.range?.[0];
  const to = props.range?.[1];
  const bucket = pickBucket(from, to);

  // The per-company table pages on the server; its state lives beside the rollup's. Like the
  // backlog stats it ranks the open load point-in-time, so the date range doesn't apply.
  const { rows, totalRecords, tableLoading, pagination } =
    useServerPagedTable<DashboardIncidentCompany>(
      (page) =>
        getDashboardIncidentCompanies({
          companyUid: props.companyUid,
          departmentUid: props.departmentUid,
          pageNumber: page,
          pageSize: COMPANIES_PAGE_SIZE,
        }),
      [props.companyUid, props.departmentUid],
      COMPANIES_PAGE_SIZE,
    );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDashboardIncidents({
      companyUid: props.companyUid,
      departmentUid: props.departmentUid,
      from,
      to,
      bucket,
    })
      .then((data) => {
        if (cancelled) return;
        setIncidents(data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.companyUid, props.departmentUid, from, to, bucket]);

  const unassigned = incidents?.unassignedOpen ?? 0;
  const highSeverity = incidents?.highSeverityOpen ?? 0;

  // Severity order pinned to the risk scale wherever severity is an axis or a stack.
  const severityIndex = (s: string) => {
    const i = SEVERITY_COLORS.domain.indexOf(s);
    return i < 0 ? SEVERITY_COLORS.domain.length : i;
  };
  const aging = (incidents?.agingBands ?? []).map((b) => ({
    type: b.band,
    series: b.severity,
    value: b.count,
  }));
  const severityStatus = [...(incidents?.severityStatus ?? [])]
    .sort((a, b) => severityIndex(a.severity) - severityIndex(b.severity))
    .map((s) => ({ type: s.severity, series: s.status, value: s.count }));
  const flow = (incidents?.flow ?? []).flatMap((p) => [
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Created", value: p.created },
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Resolved", value: p.resolved },
  ]);

  const companyColumns: ColumnsType<DashboardIncidentCompany> = [
    { title: "Company", dataIndex: "companyName" },
    { title: "Open", dataIndex: "open", align: "right" },
    { title: "High-sev", dataIndex: "highSeverityOpen", align: "right" },
    { title: "Unassigned", dataIndex: "unassignedOpen", align: "right" },
    { title: "Oldest (d)", dataIndex: "oldestOpenDays", align: "right" },
  ];

  const activeBucket = incidents?.bucket ?? bucket;

  return (
    <div className="flex w-full flex-col gap-3">
      <InsightStrip
        items={[
          `${(incidents?.openIncidents ?? 0).toLocaleString()} open`,
          unassigned > 0 ? (
            <Typography.Text type="warning">
              {unassigned.toLocaleString()} unassigned
            </Typography.Text>
          ) : (
            "0 unassigned"
          ),
          highSeverity > 0 ? (
            <Typography.Text type="warning">
              {highSeverity.toLocaleString()} high-severity
            </Typography.Text>
          ) : (
            "0 high-severity"
          ),
          `${incidents?.avgOpenAgeDays ?? 0}d avg age`,
          `${(incidents?.resolvedInPeriod ?? 0).toLocaleString()} resolved this period`,
        ]}
      />
      <Typography.Text type="secondary" className="text-xs">
        Backlog and aging are point-in-time snapshots — the date range applies to the
        created-vs-resolved flow. Resolved is approximated by an incident's last update.
      </Typography.Text>
      <div className="flex flex-row flex-wrap gap-3">
        <InsightChartCard
          title="Open backlog by age"
          subtitle="Days each open incident has been waiting, by severity"
          loading={loading}
          error={error}
        >
          {renderStackedColumn(aging, "Open incidents", SEVERITY_COLORS, 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Severity × status"
          subtitle="Where each severity sits in the workflow"
          loading={loading}
          error={error}
        >
          {renderStackedColumn(severityStatus, "Incidents", STATUS_COLORS, 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Companies"
          subtitle="Heaviest open load first"
          loading={false}
          error={error}
        >
          <Table
            size="small"
            rowKey="companyUid"
            columns={companyColumns}
            dataSource={rows}
            loading={tableLoading}
            pagination={pagination}
          />
          <Typography.Text type="secondary" className="text-xs">
            {totalRecords.toLocaleString()} companies
          </Typography.Text>
        </InsightChartCard>
        <InsightChartCard
          title="Created vs resolved"
          subtitle={`By ${activeBucket} · resolved approximated by last update`}
          loading={loading}
          error={error}
        >
          {renderDualTrend(flow, "Incidents", 260)}
        </InsightChartCard>
      </div>
    </div>
  );
}

export default IncidentsCard;
